import React, { useEffect } from 'react';

import { useProcurement } from '../context/ProcurementContext';

import HighPriorityProcurementsTable from './HighPriorityProcurementsTable';
import ProcurementColumnChart from './ProcurementColumnChart';
import ProcurementPieChart from './ProcurementPieChart';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import useApiRequest from 'Hooks/useApiRequest';
import { useCustomEvent } from 'Hooks/useGlobalDOMEvents';

export default function ProcurementDashboard(props) {
    const { routes } = useProcurement();

    const { data: stats, get: fetchStats, loading: loadingStats } = useApiRequest({ url: routes.fetchProcurementStatsUrl });

    useCustomEvent({
        eventName: 'REFETCH_PROCUREMENT_DASHBOARD',
        onSignal: () => {
            fetchStats();
        },
    });

    useEffect(() => {
        fetchStats();
    }, []);

    return (
        <div className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-px-6 tw-py-6.5">
            <div className="tw-space-y-3">
                {loadingStats ? (
                    <LoadingSpinner />
                ) : (
                    <>
                        <div>
                            <h4 className="tw-text-blue tw-text-2xl tw-font-semibold tw-mb-6">{$t('Procurement Plan')}</h4>
                            <div className="tw-flex tw-gap-1 max-lg:tw-flex-col">
                                <div className="card border-0 rounded-15px bg-white shadow tw-w-6/12 max-lg:tw-w-full">
                                    <div className="card-body card-widget dashboard__header_box">
                                        <table className="table table-sm table-borderless no-footer w-auto">
                                            <tbody>
                                                <tr>
                                                    <td>{$t('Procurements')}</td>
                                                    <td className="text-primary font-weight-bold pl-3">{stats?.procurements_count}</td>
                                                </tr>
                                                <tr>
                                                    <td>{$t('Total Non-Workforce Budget')}</td>
                                                    <td className="text-primary font-weight-bold pl-3">
                                                        {formatToFixedMoneyWithLocale(stats?.total_non_workforce_budget)}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>{$t('Total Value of Planned Procurements')}</td>
                                                    <td className="text-primary font-weight-bold pl-3">
                                                        {formatToFixedMoneyWithLocale(stats?.procurements_total_value)}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>{$t('% non-workforce budget in plan')}</td>
                                                    <td className="text-primary font-weight-bold pl-3">{`${Math.round(
                                                        stats?.procurements_budget_percentage,
                                                    )}%`}</td>
                                                </tr>
                                                <tr>
                                                    <td>{$t('Procurements behind schedule')}</td>
                                                    <td className="text-primary font-weight-bold pl-3">{stats?.procurements_overdue_count}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className={'tw-flex tw-content-center tw-justify-center tw-w-3/12 max-lg:tw-w-full'}>
                                    <ProcurementPieChart
                                        title={$t('Status')}
                                        data={stats?.procurements_count_by_status?.map((v) => v.count)}
                                        labels={stats?.procurements_count_by_status?.map((v) => v.status_name)}
                                    />
                                </div>
                                <div className={'tw-flex tw-content-center tw-justify-center tw-w-3/12 max-lg:tw-w-full'}>
                                    <ProcurementPieChart
                                        title={$t('Category')}
                                        data={stats?.procurements_count_by_category?.map((v) => v.count)}
                                        labels={stats?.procurements_count_by_category?.map((v) => v.category_name)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="tw-flex tw-mb-2 tw-gap-1 max-lg:tw-flex-col">
                                <div className={'tw-flex tw-content-center tw-justify-center tw-w-3/12 max-lg:tw-w-full'}>
                                    <ProcurementColumnChart
                                        title={$t('Raised')}
                                        data={[stats?.pr_count_by_status?.raised, stats?.po_count_by_status?.raised].filter(
                                            (v) => v !== null && v !== undefined,
                                        )}
                                    />
                                </div>
                                <div className={'tw-flex tw-content-center tw-justify-center tw-w-3/12 max-lg:tw-w-full'}>
                                    <ProcurementColumnChart
                                        title={$t('Approved')}
                                        data={[stats?.pr_count_by_status?.approved, stats?.po_count_by_status?.approved].filter(
                                            (v) => v !== null && v !== undefined,
                                        )}
                                    />
                                </div>
                                <div className={'tw-flex tw-content-center tw-justify-center tw-w-3/12 max-lg:tw-w-full'}>
                                    <ProcurementColumnChart
                                        title={$t('Awaiting Approval')}
                                        data={[stats?.pr_count_by_status?.pending, stats?.po_count_by_status?.pending].filter(
                                            (v) => v !== null && v !== undefined,
                                        )}
                                    />
                                </div>
                                <div className={'tw-flex tw-content-center tw-justify-center tw-w-3/12 max-lg:tw-w-full'}>
                                    <ProcurementColumnChart
                                        title={$t('Rejected')}
                                        data={[stats?.pr_count_by_status?.rejected, stats?.po_count_by_status?.rejected].filter(
                                            (v) => v !== null && v !== undefined,
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}
                <div>
                    <HighPriorityProcurementsTable />
                </div>
            </div>
        </div>
    );
}
